import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Settings } from 'luxon';

import enCommon from '@/locales/en/common.json';
import frCommon from '@/locales/fr/common.json';
import enTerms from '@/locales/en/terms-and-conditions.json';
import frTerms from '@/locales/fr/terms-and-conditions.json';
import { StripeElementLocale } from '@stripe/stripe-js';

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		resources: {
			en: { common: enCommon, terms: enTerms },
			fr: { common: frCommon, terms: frTerms }
		},
		fallbackLng: 'en',
		supportedLngs: ['en', 'fr'],
		defaultNS: 'common',
		ns: ['common', 'terms'],
		interpolation: { escapeValue: false },
		detection: {
			order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
			lookupQuerystring: 'lang',
			caches: ['localStorage']
		}
	});

export const validateAndCorrectLanguage = (supportedLocales: string[] = ['en']) => {
	const currentLanguage = i18n.language?.startsWith('fr') ? 'fr' : 'en';
	const isCurrentLanguageSupported = supportedLocales.includes(currentLanguage);

	if (!isCurrentLanguageSupported) {
		const fallbackLanguage = supportedLocales.includes('en') ? 'en' : (supportedLocales[0] || 'en');
		i18n.changeLanguage(fallbackLanguage);
		return fallbackLanguage;
	}

	return currentLanguage;
};

// Handle URL language parameter on app initialization and remove it
const urlParams = new URLSearchParams(window.location.search);
const langParam = urlParams.get('lang');
if (langParam && ['en', 'fr'].includes(langParam)) {
	i18n.changeLanguage(langParam);
	// Remove the lang parameter from URL
	urlParams.delete('lang');
	const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '') + window.location.hash;
	window.history.replaceState({}, '', newUrl);
}

// Set initial Luxon locale
Settings.defaultLocale = 'en';

export default i18n;

i18n.on('languageChanged', (lng) => {
	if (typeof document !== 'undefined') {
		document.documentElement.lang = lng || 'en';
	}
	// Update Luxon locale to match i18n language
	Settings.defaultLocale = lng === 'fr' ? 'fr' : 'en';
});

const STRIPE_SUPPORTED_LOCALES: StripeElementLocale[] = ['en', 'fr', 'en-AU', 'en-CA', 'en-GB', 'en-NZ', 'en', 'es', 'es-419', 'es-ES', 'fr-CA', 'fr-FR', 'it', 'it-IT', 'ja', 'ko', 'nl', 'pl', 'pt-BR', 'ru', 'tr', 'zh-HK', 'zh-TW'];
export const getStripeLocale = (lng: string): StripeElementLocale => {
	if (STRIPE_SUPPORTED_LOCALES.includes(lng as StripeElementLocale)) {
		return lng as StripeElementLocale;
	}
	return 'en';
};
